import Phaser from 'phaser';
import { Behavior, BehaviorConfig } from './Behavior';
import { Entity } from '../base/Entity';

export interface EightDirectionConfig extends BehaviorConfig {
  maxSpeed?: number;
  acceleration?: number;
  deceleration?: number;
  directions?: 4 | 8;
  setAngle?: boolean;
  defaultControls?: boolean;
  sprintMultiplier?: number;  // Speed multiplier when holding Shift (default: 20)
}

/**
 * EightDirectionBehavior - 8-direction movement with keyboard input
 *
 * Equivalent to Construct 3's "8 Direction" behavior.
 * Handles keyboard input (arrows + WASD) and applies smooth acceleration/deceleration.
 *
 * Config from C3:
 * - max-speed: 800
 * - acceleration: 1200
 * - deceleration: 1000
 */
export class EightDirectionBehavior extends Behavior {
  // Movement properties
  public maxSpeed: number;
  public acceleration: number;
  public deceleration: number;
  public directions: 4 | 8;
  public setAngle: boolean;
  public defaultControls: boolean;
  public sprintMultiplier: number;

  // Current velocity
  private velocityX: number = 0;
  private velocityY: number = 0;

  // Input state
  private inputX: number = 0;
  private inputY: number = 0;

  // Keyboard keys
  private cursors?: Phaser.Types.Input.Keyboard.CursorKeys;
  private wasd?: {
    W: Phaser.Input.Keyboard.Key;
    A: Phaser.Input.Keyboard.Key;
    S: Phaser.Input.Keyboard.Key;
    D: Phaser.Input.Keyboard.Key;
  };
  private shiftKey?: Phaser.Input.Keyboard.Key;

  constructor(entity: Entity, config: EightDirectionConfig = {}) {
    super(entity, config);

    // Apply C3-like defaults
    this.maxSpeed = config.maxSpeed ?? 800;
    this.acceleration = config.acceleration ?? 1200;
    this.deceleration = config.deceleration ?? 1000;
    this.directions = config.directions ?? 8;
    this.setAngle = config.setAngle ?? false;
    this.defaultControls = config.defaultControls ?? true;
    this.sprintMultiplier = config.sprintMultiplier ?? 20;

    // Disable gravity for flying entity
    if (entity.body) {
      (entity.body as Phaser.Physics.Arcade.Body).setAllowGravity(false);
    }

    // Setup keyboard input
    if (this.defaultControls) {
      this.setupKeyboard();
    }
  }

  /**
   * Setup keyboard controls (arrows + WASD)
   */
  private setupKeyboard(): void {
    if (!this.scene.input.keyboard) return;

    // Arrow keys
    this.cursors = this.scene.input.keyboard.createCursorKeys();

    // WASD keys
    this.wasd = {
      W: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.W),
      A: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.A),
      S: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.S),
      D: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.D),
    };

    // Shift key for sprint
    this.shiftKey = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SHIFT);
  }

  /**
   * Read input and update velocity each frame
   */
  update(time: number, delta: number): void {
    if (!this.enabled) return;

    // Read input
    this.readInput();

    // Apply acceleration/deceleration
    this.updateVelocity(delta);

    // Apply velocity to entity
    this.applyVelocity();

    // Optionally set angle based on movement direction
    if (this.setAngle && (this.velocityX !== 0 || this.velocityY !== 0)) {
      this.entity.angle = Phaser.Math.RadToDeg(
        Math.atan2(this.velocityY, this.velocityX)
      );
    }
  }

  /**
   * Read keyboard input and set input direction
   */
  private readInput(): void {
    if (!this.defaultControls) return;

    this.inputX = 0;
    this.inputY = 0;

    // Check arrows
    if (this.cursors) {
      if (this.cursors.left.isDown) this.inputX -= 1;
      if (this.cursors.right.isDown) this.inputX += 1;
      if (this.cursors.up.isDown) this.inputY -= 1;
      if (this.cursors.down.isDown) this.inputY += 1;
    }

    // Check WASD (if not already moving from arrows)
    if (this.wasd) {
      if (this.wasd.A.isDown) this.inputX -= 1;
      if (this.wasd.D.isDown) this.inputX += 1;
      if (this.wasd.W.isDown) this.inputY -= 1;
      if (this.wasd.S.isDown) this.inputY += 1;
    }

    // Clamp to -1, 0, 1
    this.inputX = Phaser.Math.Clamp(this.inputX, -1, 1);
    this.inputY = Phaser.Math.Clamp(this.inputY, -1, 1);

    // If 4 directions only, prioritize horizontal
    if (this.directions === 4 && this.inputX !== 0 && this.inputY !== 0) {
      this.inputY = 0;
    }
  }

  /**
   * Update velocity based on input and acceleration/deceleration
   */
  private updateVelocity(delta: number): void {
    const dt = delta / 1000; // Convert to seconds

    // Apply sprint multiplier if Shift is held
    const isSprinting = this.shiftKey?.isDown ?? false;
    const sprintFactor = isSprinting ? this.sprintMultiplier : 1;
    const currentMaxSpeed = this.maxSpeed * sprintFactor;
    const currentAcceleration = this.acceleration * sprintFactor;
    const currentDeceleration = this.deceleration * sprintFactor;

    // Calculate target velocity based on input
    let targetX = this.inputX * currentMaxSpeed;
    let targetY = this.inputY * currentMaxSpeed;

    // Normalize diagonal movement to prevent faster diagonal speed
    if (this.inputX !== 0 && this.inputY !== 0) {
      const diagonalFactor = 1 / Math.SQRT2; // ~0.707
      targetX *= diagonalFactor;
      targetY *= diagonalFactor;
    }

    // Apply acceleration or deceleration
    if (this.inputX !== 0) {
      // Accelerate toward target
      this.velocityX = this.approach(
        this.velocityX,
        targetX,
        currentAcceleration * dt
      );
    } else {
      // Decelerate to zero
      this.velocityX = this.approach(this.velocityX, 0, currentDeceleration * dt);
    }

    if (this.inputY !== 0) {
      this.velocityY = this.approach(
        this.velocityY,
        targetY,
        currentAcceleration * dt
      );
    } else {
      this.velocityY = this.approach(this.velocityY, 0, currentDeceleration * dt);
    }

    // Clamp velocity to current max speed (prevents runaway speed when releasing sprint)
    const currentSpeed = Math.sqrt(this.velocityX ** 2 + this.velocityY ** 2);
    if (currentSpeed > currentMaxSpeed) {
      const scale = currentMaxSpeed / currentSpeed;
      this.velocityX *= scale;
      this.velocityY *= scale;
    }
  }

  /**
   * Check if currently sprinting (Shift held)
   */
  isSprinting(): boolean {
    return this.shiftKey?.isDown ?? false;
  }

  /**
   * Move current value toward target by amount
   */
  private approach(current: number, target: number, amount: number): number {
    if (current < target) {
      return Math.min(current + amount, target);
    } else if (current > target) {
      return Math.max(current - amount, target);
    }
    return target;
  }

  /**
   * Apply calculated velocity to entity physics body
   */
  private applyVelocity(): void {
    if (!this.entity.body) return;

    const body = this.entity.body as Phaser.Physics.Arcade.Body;
    body.setVelocity(this.velocityX, this.velocityY);
  }

  /**
   * Simulate input (for AI or external control)
   */
  simulateInput(x: number, y: number): void {
    this.inputX = Phaser.Math.Clamp(x, -1, 1);
    this.inputY = Phaser.Math.Clamp(y, -1, 1);
  }

  /**
   * Stop all movement immediately
   */
  stop(): void {
    this.velocityX = 0;
    this.velocityY = 0;
    this.inputX = 0;
    this.inputY = 0;

    if (this.entity.body) {
      (this.entity.body as Phaser.Physics.Arcade.Body).setVelocity(0, 0);
    }
  }

  /**
   * Check if currently moving
   */
  isMoving(): boolean {
    return this.velocityX !== 0 || this.velocityY !== 0;
  }

  /**
   * Get current speed
   */
  getSpeed(): number {
    return Math.sqrt(this.velocityX ** 2 + this.velocityY ** 2);
  }

  /**
   * Get movement direction in radians
   */
  getDirection(): number {
    return Math.atan2(this.velocityY, this.velocityX);
  }

  /**
   * Clean up keyboard listeners
   */
  destroy(): void {
    // Keys are managed by the scene's keyboard plugin, no need to destroy
    this.cursors = undefined;
    this.wasd = undefined;
    this.shiftKey = undefined;
    super.destroy();
  }
}
